(() => {
  'use strict';

  const LEAD_EVENT_NAME = 'vianne:lead_captured';
  const CONTEXT_EVENT_NAME = 'vianne:analytics_context';
  const CTA_EVENT_NAME = 'vianne:cta_clicked';
  const BROWSER_CONTEXT_V2_FLAG = '__viannePostHogBrowserContextV2';
  const SCRIPT_SELECTOR = 'script[data-vianne-analytics-events]';
  const CTA_SELECTOR = '[data-vianne-cta-action][data-vianne-cta-placement]';
  const STATE_KEY = Symbol.for('vianne.analytics.events.state.v1');
  const THEME_ROLES = new Set(['main', 'unpublished', 'development', 'demo']);
  const CTA_PLACEMENTS_BY_ACTION = Object.freeze({
    add_to_cart: new Set([
      'featured_product',
      'featured_product_sticky',
      'product_main',
      'product_sticky',
      'quick_view',
    ]),
    buy_now: new Set(['featured_product', 'product_main', 'product_sticky', 'quick_view']),
    begin_checkout: new Set(['cart_drawer', 'cart_notification', 'cart_page']),
    continue_shopping: new Set(['cart_drawer', 'cart_page']),
    newsletter_submit: new Set(['footer', 'newsletter_popup', 'newsletter_section']),
    open_cart: new Set(['header']),
    open_quick_view: new Set(['product_card']),
    open_search: new Set(['header']),
    return_to_purchase: new Set(['pain_point_closing', 'product_closing']),
    view_cart: new Set(['cart_drawer', 'cart_notification']),
    view_product: new Set([
      'featured_product_sticky',
      'home_hero',
      'home_product',
      'pain_point_closing',
      'pain_point_proof',
      'quick_view',
    ]),
  });
  const CTA_DEBOUNCE_MS = 800;
  const analyticsState = window[STATE_KEY] || {
    ctaListenerInstalled: false,
    recentCtaClicks: new Map(),
  };
  window[STATE_KEY] = analyticsState;
  const MAX_ATTEMPTS = 20;
  const RETRY_DELAY_MS = 250;

  function normalizePlacement(value) {
    if (value === 'contactPopup') return 'popup';
    if (value === 'footer') return 'footer';
    return 'section';
  }

  function readAnalyticsContext() {
    const script = document.querySelector(SCRIPT_SELECTOR);
    if (!script) return undefined;

    const themeId = String(script.dataset.shopifyThemeId || '').trim();
    const themeRole = String(script.dataset.shopifyThemeRole || '').trim();
    const analyticsBuildId = String(script.dataset.analyticsBuildId || '').trim();
    const designMode = String(script.dataset.shopifyDesignMode || '').trim();

    if (!/^\d{1,24}$/.test(themeId)) return undefined;
    if (!THEME_ROLES.has(themeRole)) return undefined;
    if (!/^[a-z0-9._-]{1,64}$/i.test(analyticsBuildId)) return undefined;
    if (designMode !== 'true' && designMode !== 'false') return undefined;

    return {
      schema_version: 1,
      theme_id: themeId,
      theme_role: themeRole,
      design_mode: designMode === 'true',
      analytics_build_id: analyticsBuildId,
      browser_context_v2_available: window[BROWSER_CONTEXT_V2_FLAG] === true,
    };
  }

  function readCta(element) {
    const action = String(element?.dataset?.vianneCtaAction || '').trim();
    const placement = String(element?.dataset?.vianneCtaPlacement || '').trim();
    if (!Object.prototype.hasOwnProperty.call(CTA_PLACEMENTS_BY_ACTION, action)) return undefined;
    if (!CTA_PLACEMENTS_BY_ACTION[action].has(placement)) return undefined;
    return { action, placement };
  }

  function publishCtaClick(event) {
    const element = event?.target?.closest?.(CTA_SELECTOR);
    const cta = readCta(element);
    const publisher = window.Shopify?.analytics?.publish;
    if (!cta || typeof publisher !== 'function') return;

    if (event?.target?.closest?.(':disabled, [aria-disabled="true"]')) return;
    const clickKey = `${cta.action}:${cta.placement}`;
    const now = Date.now();
    const previousClickAt = analyticsState.recentCtaClicks.get(clickKey);
    if (typeof previousClickAt === 'number' && now - previousClickAt < CTA_DEBOUNCE_MS) return;
    analyticsState.recentCtaClicks.set(clickKey, now);

    publisher(CTA_EVENT_NAME, cta);
  }

  function publishAnalyticsEvents(attempt = 0) {
    const publisher = window.Shopify?.analytics?.publish;

    if (typeof publisher !== 'function') {
      if (attempt < MAX_ATTEMPTS) {
        window.setTimeout(() => publishAnalyticsEvents(attempt + 1), RETRY_DELAY_MS);
      }
      return;
    }

    const script = document.querySelector(SCRIPT_SELECTOR);
    const analyticsContext = readAnalyticsContext();
    if (script && analyticsContext && script.dataset.vianneContextPublished !== 'true') {
      script.dataset.vianneContextPublished = 'true';
      publisher(CONTEXT_EVENT_NAME, analyticsContext);
    }

    document.querySelectorAll('[data-vianne-lead-captured]').forEach((element) => {
      if (element.dataset.vianneLeadPublished === 'true') return;

      element.dataset.vianneLeadPublished = 'true';
      publisher(LEAD_EVENT_NAME, {
        placement: normalizePlacement(element.dataset.vianneLeadPlacement),
      });
    });
  }

  publishAnalyticsEvents();

  const analyticsScript = document.querySelector(SCRIPT_SELECTOR);
  if (analyticsScript && !analyticsState.ctaListenerInstalled) {
    analyticsState.ctaListenerInstalled = true;
    document.addEventListener('click', publishCtaClick, true);
  }
  document.addEventListener('shopify:section:load', () => publishAnalyticsEvents());
})();
